import React, { createContext, useContext, useRef, useCallback } from 'react';

interface UnsavedChangesContextType {
  registerChecker: (checker: () => boolean) => void;
  unregisterChecker: () => void;
  checkUnsavedChanges: () => boolean;
  requestNavigation: (onConfirm: () => void) => boolean;
  setModalHandler: (handler: ((onConfirm: () => void) => void) | null) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | undefined>(undefined);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const checkerRef = useRef<(() => boolean) | null>(null);
  const modalHandlerRef = useRef<((onConfirm: () => void) => void) | null>(null);

  const registerChecker = useCallback((checker: () => boolean) => {
    checkerRef.current = checker;
  }, []);

  const unregisterChecker = useCallback(() => {
    checkerRef.current = null;
  }, []);

  const checkUnsavedChanges = useCallback(() => {
    return checkerRef.current ? checkerRef.current() : false;
  }, []);

  const setModalHandler = useCallback((handler: ((onConfirm: () => void) => void) | null) => {
    modalHandlerRef.current = handler;
  }, []);

  const requestNavigation = useCallback((onConfirm: () => void) => {
    if (checkerRef.current && checkerRef.current()) {
      if (modalHandlerRef.current) {
        modalHandlerRef.current(onConfirm);
        return false; // Navigation blocked
      }
    }
    return true; // Navigation allowed
  }, []);

  return (
    <UnsavedChangesContext.Provider
      value={{
        registerChecker,
        unregisterChecker,
        checkUnsavedChanges,
        requestNavigation,
        setModalHandler,
      }}
    >
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChanges = () => {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChanges must be used within UnsavedChangesProvider');
  }
  return context;
};
